import React, {useEffect, useRef} from 'react';
import {useUpdateNodeInternals} from 'reactflow';

import {ControlledAddNodeHandle} from 'ui-library/reactflow/handles/AddNodeHandle';
import {InvisibleNodeHandle} from 'ui-library/reactflow/handles/InvisibleNodeHandle';
import {NodeHandleProps} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeTypes';
import {defaultNodeHandleIdGenerator} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';

export interface EntityNodeHandleProps extends Omit<NodeHandleProps, 'handleType'> {
  isFullyExpanded: boolean;
  isExpandable: boolean;
  onClick: () => void;
  tooltipText?: string;
  isHovered: boolean;
  setIsHovered: (isHovered: boolean) => void;
}

export default function EntityNodeSourceHandle(props: EntityNodeHandleProps): JSX.Element {
  const updateNodeInternals = useUpdateNodeInternals();
  const prevFullyExpandedRef = useRef<boolean>(props.isFullyExpanded);

  // When a node is fully expanded, we switch to an invisible handle, which has a different size and position
  // than the AddNodeHandle. We need to update the node internals to ensure that edges point to this new
  // location. See https://reactflow.dev/api-reference/hooks/use-update-node-internals
  // We don't want to run this on initial render, so we explicitly track when isFullyExpanded changes
  useEffect(() => {
    if (prevFullyExpandedRef.current !== props.isFullyExpanded) {
      updateNodeInternals(props.nodeId);
    }
    prevFullyExpandedRef.current = props.isFullyExpanded;
  }, [props.isFullyExpanded, props.nodeId, updateNodeInternals]);

  if (props.isFullyExpanded) {
    return <InvisibleNodeHandle nodeId={props.nodeId} handleType="source" />;
  }

  return (
    <ControlledAddNodeHandle
      id={defaultNodeHandleIdGenerator(props.nodeId, 'source')}
      disabled={!props.isExpandable}
      type="source"
      isConnectable={false}
      onClick={props.onClick}
      tooltipText={props.tooltipText}
      displayHandleAsHovered={props.isHovered}
      setDisplayHandleAsHovered={props.setIsHovered}
    />
  );
}
